<template>
	<div class="ai-email-page">
		<section class="page-section">
			<div class="section-heading">
				<div>
					<h3>{{ $t("aiEmail.account") }}</h3>
					<p>{{ $t("aiEmail.accountDescription") }}</p>
				</div>
				<el-button type="primary" size="small" @click="openAccountDialog" :disabled="accountLoading">
					{{ account ? $t("aiEmail.editAccount") : $t("aiEmail.configureAccount") }}
				</el-button>
			</div>

			<el-card v-loading="accountLoading" :element-loading-text="$t('aiEmail.loadingAccount')" class="account-card">
				<template v-if="account">
					<div class="account-overview">
						<div>
							<div class="account-email">{{ account.email }}</div>
							<el-tag :type="accountStatusType" size="mini">{{ accountStatusText }}</el-tag>
						</div>
						<div class="status-control">
							<div class="status-switch">
								<span class="status-switch-text">{{ account.is_enabled ? $t("aiEmail.enabled") : $t("aiEmail.disabled") }}</span>
								<el-switch
									:key="accountStatusKey"
									:value="account.is_enabled"
									:disabled="accountStatusUpdating"
									:aria-label="$t('aiEmail.accountEnabled')"
									@change="handleAccountStatusChange"
								></el-switch>
							</div>
							<i v-if="accountStatusUpdating" class="el-icon-loading status-loading" aria-hidden="true"></i>
							<p v-if="accountStatusError" class="field-error" role="alert">{{ accountStatusError }}</p>
						</div>
					</div>

					<dl class="account-details">
						<div>
							<dt>{{ $t("aiEmail.latestSync") }}</dt>
							<dd>{{ formatDate(account.last_synced_at) }}</dd>
						</div>
						<div v-if="account.last_error" class="sync-error">
							<dt>{{ $t("aiEmail.syncError") }}</dt>
							<dd>{{ account.last_error }}</dd>
						</div>
					</dl>
				</template>
				<div v-else class="account-empty">
					<p>{{ $t("aiEmail.accountEmpty") }}</p>
					<el-button type="primary" @click="openAccountDialog">{{ $t("aiEmail.configureAccount") }}</el-button>
				</div>
			</el-card>

			<el-alert
				v-if="account && !account.is_enabled"
				class="disabled-notice"
				:type="account.last_error ? 'warning' : 'info'"
				:closable="false"
				show-icon
				:title="$t('aiEmail.emailDisabledNotice')"
			></el-alert>
		</section>

		<section class="page-section">
			<div class="section-heading">
				<div>
					<h3>{{ $t("aiEmail.contacts") }}</h3>
					<p>{{ $t("aiEmail.contactsDescription") }}</p>
				</div>
				<el-button type="primary" size="small" @click="openCreateContactDialog">{{ $t("aiEmail.addContact") }}</el-button>
			</div>

			<el-card class="contacts-card">
				<el-table :data="contacts" v-loading="contactsLoading" :element-loading-text="$t('aiEmail.loadingContacts')" border style="width: 100%">
					<el-table-column prop="email" :label="$t('aiEmail.guestEmail')" min-width="200" :resizable="false"></el-table-column>
					<el-table-column :label="$t('aiEmail.processingMode')" width="160" :resizable="false">
						<template slot-scope="scope">
							<el-tag :type="scope.row.mode === 'assistant' ? 'success' : 'info'" size="mini">{{ modeLabel(scope.row.mode) }}</el-tag>
						</template>
					</el-table-column>
					<el-table-column :label="$t('aiEmail.associatedCustomers')" min-width="280" :resizable="false">
						<template slot-scope="scope">
							<div class="customer-tags">
								<el-tag v-for="customerId in scope.row.customer_ids" :key="customerId" size="mini">{{ customerId | removeFrontZero }}</el-tag>
							</div>
						</template>
					</el-table-column>
					<el-table-column :label="$t('aiEmail.updatedAt')" width="180" :resizable="false">
						<template slot-scope="scope">{{ formatDate(scope.row.updated_at) }}</template>
					</el-table-column>
					<el-table-column :label="$t('aiEmail.actions')" width="140" fixed="right" :resizable="false">
						<template slot-scope="scope">
							<el-button type="text" size="small" @click="openEditContactDialog(scope.row)">{{ $t("aiEmail.edit") }}</el-button>
							<el-button type="text" size="small" class="delete-button" @click="confirmDeleteContact(scope.row)">{{
								$t("aiEmail.delete")
							}}</el-button>
						</template>
					</el-table-column>
				</el-table>
			</el-card>
		</section>

		<el-dialog
			:title="account ? $t('aiEmail.editAccount') : $t('aiEmail.configureAccount')"
			:visible.sync="accountDialogVisible"
			:close-on-click-modal="false"
			:close-on-press-escape="false"
			width="520px"
			@closed="resetAccountDialog"
		>
			<el-form ref="accountForm" :model="accountForm" :rules="accountRules" label-width="120px">
				<el-form-item :label="$t('aiEmail.emailAddress')" prop="email" :error="accountErrors.email">
					<el-input v-model="accountForm.email" type="email" autocomplete="email" @input="clearAccountError('email')"></el-input>
				</el-form-item>
				<el-form-item :label="$t('aiEmail.emailPassword')" prop="password" :error="accountErrors.password">
					<el-input
						v-model="accountForm.password"
						type="password"
						show-password
						autocomplete="new-password"
						@input="clearAccountError('password')"
					></el-input>
					<p v-if="account && account.password_configured" class="form-help">{{ $t("aiEmail.passwordSetHint") }}</p>
				</el-form-item>
			</el-form>
			<div slot="footer" class="dialog-footer">
				<el-button @click="accountDialogVisible = false" :disabled="accountSaving">{{ $t("common.cancel") }}</el-button>
				<el-button type="primary" :loading="accountSaving" :disabled="accountSaving" @click="saveAccount">
					{{ accountSaving ? $t("aiEmail.validatingConnection") : $t("aiEmail.save") }}
				</el-button>
			</div>
		</el-dialog>

		<el-dialog
			:title="contactEditingId ? $t('aiEmail.editContact') : $t('aiEmail.addContact')"
			:visible.sync="contactDialogVisible"
			:close-on-click-modal="false"
			:close-on-press-escape="false"
			width="620px"
			@closed="resetContactDialog"
		>
			<el-form ref="contactForm" :model="contactForm" :rules="contactRules" label-width="130px">
				<el-form-item :label="$t('aiEmail.guestEmail')" prop="email" :error="contactErrors.email">
					<el-input v-model="contactForm.email" type="email" autocomplete="email" @input="clearContactError('email')"></el-input>
				</el-form-item>
				<el-form-item :label="$t('aiEmail.processingMode')" prop="mode" :error="contactErrors.mode">
					<el-radio-group v-model="contactForm.mode" @change="clearContactError('mode')">
						<el-radio label="assistant">{{ $t("aiEmail.automatic") }}</el-radio>
						<el-radio label="order_intent">{{ $t("aiEmail.semiAutomatic") }}</el-radio>
					</el-radio-group>
					<p class="form-help">
						{{ contactForm.mode === "assistant" ? $t("aiEmail.automaticDescription") : $t("aiEmail.semiAutomaticDescription") }}
					</p>
				</el-form-item>
				<el-form-item :label="$t('aiEmail.associatedCustomers')" prop="customer_ids" :error="contactErrors.customer_ids">
					<el-select
						v-model="contactForm.customer_ids"
						multiple
						filterable
						:disabled="!customers.length"
						:placeholder="$t('aiEmail.customerPlaceholder')"
						style="width: 100%"
						@change="clearContactError('customer_ids')"
					>
						<el-option
							v-for="customer in customers"
							:key="customer.customer_id"
							:label="customerOptionLabel(customer)"
							:value="customer.customer_id"
						></el-option>
					</el-select>
					<p v-if="!customersLoading && !customers.length" class="field-error" role="alert">{{ $t("aiEmail.noCustomers") }}</p>
				</el-form-item>
			</el-form>
			<div slot="footer" class="dialog-footer">
				<el-button @click="contactDialogVisible = false" :disabled="contactSaving">{{ $t("common.cancel") }}</el-button>
				<el-button type="primary" :loading="contactSaving" :disabled="contactSaving || !customers.length" @click="saveContact">
					{{ $t("aiEmail.save") }}
				</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
import moment from "moment";
import {
	createEmailContact,
	deleteEmailContact,
	getEmailAccount,
	getEmailContact,
	getEmailContactCustomers,
	getEmailContacts,
	saveEmailAccount,
	updateEmailAccountStatus,
	updateEmailContact,
} from "@/services/api-v2/api-v2";

export default {
	name: "AiEmail",

	data() {
		return {
			account: null,
			contacts: [],
			customers: [],
			accountLoading: false,
			contactsLoading: false,
			customersLoading: false,
			accountSaving: false,
			accountStatusUpdating: false,
			accountStatusKey: 0,
			contactSaving: false,
			accountDialogVisible: false,
			contactDialogVisible: false,
			contactEditingId: null,
			accountStatusError: "",
			accountErrors: {},
			contactErrors: {},
			accountForm: {
				email: "",
				password: "",
			},
			contactForm: {
				email: "",
				mode: "assistant",
				customer_ids: [],
			},
		};
	},

	computed: {
		accountStatusType() {
			if (!this.account) return "info";
			if (this.account.last_error) return "danger";
			return this.account.is_enabled ? "success" : "info";
		},

		accountStatusText() {
			if (!this.account) return "";
			if (this.account.last_error) return this.$t("aiEmail.syncError");
			return this.account.is_enabled ? this.$t("aiEmail.enabled") : this.$t("aiEmail.disabled");
		},

		accountRules() {
			return {
				email: [
					{ required: true, message: this.$t("aiEmail.validationEmail"), trigger: "blur" },
					{ type: "email", message: this.$t("aiEmail.validationEmail"), trigger: "blur" },
				],
				password: [{ validator: this.validateAccountPassword, trigger: "blur" }],
			};
		},

		contactRules() {
			return {
				email: [
					{ required: true, message: this.$t("aiEmail.validationEmail"), trigger: "blur" },
					{ type: "email", message: this.$t("aiEmail.validationEmail"), trigger: "blur" },
				],
				mode: [{ required: true, message: this.$t("aiEmail.validationMode"), trigger: "change" }],
				customer_ids: [{ type: "array", required: true, min: 1, message: this.$t("aiEmail.validationCustomers"), trigger: "change" }],
			};
		},
	},

	created() {
		this.loadPageData();
	},

	methods: {
		loadPageData() {
			return Promise.all([this.loadAccount(), this.loadContacts(), this.loadCustomers()]);
		},

		async loadAccount() {
			this.accountLoading = true;
			try {
				const { data } = await getEmailAccount();
				this.account = data;
			} catch (error) {
				this.showError(error);
			} finally {
				this.accountLoading = false;
			}
		},

		async loadContacts() {
			this.contactsLoading = true;
			try {
				const { data } = await getEmailContacts();
				this.contacts = data;
			} catch (error) {
				this.showError(error);
			} finally {
				this.contactsLoading = false;
			}
		},

		async loadCustomers() {
			this.customersLoading = true;
			try {
				const { data } = await getEmailContactCustomers();
				this.customers = data;
			} catch (error) {
				this.showError(error);
			} finally {
				this.customersLoading = false;
			}
		},

		openAccountDialog() {
			this.accountErrors = {};
			this.accountStatusError = "";
			this.accountForm = {
				email: this.account ? this.account.email : "",
				password: "",
			};
			this.accountDialogVisible = true;
			this.$nextTick(() => this.$refs.accountForm.clearValidate());
		},

		resetAccountDialog() {
			this.accountErrors = {};
			this.accountForm = {
				email: "",
				password: "",
			};
			if (this.$refs.accountForm) this.$refs.accountForm.clearValidate();
		},

		validateAccountPassword(rule, value, callback) {
			if ((!this.account || !this.account.password_configured) && !value) {
				callback(new Error(this.$t("aiEmail.validationPassword")));
				return;
			}

			callback();
		},

		async saveAccount() {
			try {
				await this.$refs.accountForm.validate();
			} catch (error) {
				return;
			}

			const payload = {
				email: this.accountForm.email.trim(),
			};

			if (this.accountForm.password !== "") payload.password = this.accountForm.password;

			this.accountSaving = true;
			this.accountErrors = {};
			try {
				const { data } = await saveEmailAccount(payload);
				this.account = data;
				this.accountDialogVisible = false;
				this.$message.success(this.$t("aiEmail.accountSaveSuccess"));
			} catch (error) {
				if (!this.applyAccountErrors(error)) this.showError(error);
			} finally {
				this.accountSaving = false;
			}
		},

		async handleAccountStatusChange(isEnabled) {
			if (!this.account) return;

			this.accountStatusUpdating = true;
			this.accountStatusError = "";
			try {
				const { data } = await updateEmailAccountStatus(isEnabled);
				this.account = data;
				this.$message.success(this.$t("aiEmail.statusUpdateSuccess"));
			} catch (error) {
				if (!this.applyAccountErrors(error)) this.showError(error);
				this.accountStatusKey += 1;
			} finally {
				this.accountStatusUpdating = false;
			}
		},

		openCreateContactDialog() {
			this.contactEditingId = null;
			this.contactErrors = {};
			this.contactForm = {
				email: "",
				mode: "assistant",
				customer_ids: [],
			};
			this.contactDialogVisible = true;
			this.$nextTick(() => this.$refs.contactForm.clearValidate());
		},

		async openEditContactDialog(contact) {
			this.contactErrors = {};
			try {
				const { data } = await getEmailContact(contact.id);
				this.contactEditingId = data.id;
				this.contactForm = {
					email: data.email,
					mode: data.mode,
					customer_ids: data.customer_ids.slice(),
				};
				this.contactDialogVisible = true;
				this.$nextTick(() => this.$refs.contactForm.clearValidate());
			} catch (error) {
				this.showError(error);
				if (this.isNotFoundError(error)) await this.loadContacts();
			}
		},

		resetContactDialog() {
			this.contactEditingId = null;
			this.contactErrors = {};
			this.contactForm = {
				email: "",
				mode: "assistant",
				customer_ids: [],
			};
			if (this.$refs.contactForm) this.$refs.contactForm.clearValidate();
		},

		async saveContact() {
			try {
				await this.$refs.contactForm.validate();
			} catch (error) {
				return;
			}

			const payload = {
				email: this.contactForm.email.trim(),
				mode: this.contactForm.mode,
				customer_ids: this.contactForm.customer_ids.slice(),
			};

			this.contactSaving = true;
			this.contactErrors = {};
			const isEditing = Boolean(this.contactEditingId);
			try {
				if (isEditing) {
					await updateEmailContact(this.contactEditingId, payload);
				} else {
					await createEmailContact(payload);
				}
				await this.loadContacts();
				this.contactDialogVisible = false;
				this.$message.success(this.$t(isEditing ? "aiEmail.contactUpdateSuccess" : "aiEmail.contactCreateSuccess"));
			} catch (error) {
				if (!this.applyContactErrors(error)) this.showError(error);
				if (this.isNotFoundError(error)) await this.loadContacts();
			} finally {
				this.contactSaving = false;
			}
		},

		async confirmDeleteContact(contact) {
			try {
				await this.$confirm(this.$t("aiEmail.deleteContactMessage", { email: contact.email }), this.$t("aiEmail.confirmDelete"), {
					confirmButtonText: this.$t("common.confirm"),
					cancelButtonText: this.$t("common.cancel"),
					type: "warning",
				});
			} catch (error) {
				return;
			}

			try {
				await deleteEmailContact(contact.id);
				await this.loadContacts();
				this.$message.success(this.$t("aiEmail.contactDeleteSuccess"));
			} catch (error) {
				this.showError(error);
				if (this.isNotFoundError(error)) await this.loadContacts();
			}
		},

		applyAccountErrors(error) {
			if (!error || !error.errors) return false;

			this.accountErrors = this.normalizeErrors(error.errors);
			this.accountStatusError = this.accountErrors.is_enabled || "";
			return true;
		},

		applyContactErrors(error) {
			if (!error || !error.errors) return false;

			this.contactErrors = this.normalizeErrors(error.errors);
			return true;
		},

		normalizeErrors(errors) {
			return Object.keys(errors).reduce((result, field) => {
				result[field] = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
				return result;
			}, {});
		},

		clearAccountError(field) {
			this.$delete(this.accountErrors, field);
			if (field === "is_enabled") this.accountStatusError = "";
		},

		clearContactError(field) {
			this.$delete(this.contactErrors, field);
		},

		isNotFoundError(error) {
			return error && error.httpStatus === 404;
		},

		showError(error) {
			if (!error) return;

			const message = typeof error === "string" ? error : error.message;
			this.$message.error(message || this.$t("apiJS.unknownMistake"));
		},

		modeLabel(mode) {
			return mode === "assistant" ? this.$t("aiEmail.automatic") : this.$t("aiEmail.semiAutomatic");
		},

		customerOptionLabel(customer) {
			return `${customer.name} · ${customer.customer_id} · ${customer.address}`;
		},

		formatDate(value) {
			if (!value) return this.$t("aiEmail.noSyncTime");
			return moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss");
		},
	},
};
</script>

<style lang="scss" scoped>
.ai-email-page {
	width: 100%;
	padding-bottom: 24px;
	box-sizing: border-box;

	.page-section,
	.account-card,
	.contacts-card {
		width: 100%;
		box-sizing: border-box;
	}

	.page-section + .page-section {
		margin-top: 28px;
	}

	.section-heading {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 16px;
		margin-bottom: 12px;

		h3 {
			margin: 0 0 6px;
			font-size: 18px;
		}

		p {
			margin: 0;
			color: #909399;
			font-size: 13px;
		}
	}

	.account-overview {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 20px;
	}

	.account-email {
		margin-bottom: 8px;
		font-size: 18px;
		font-weight: 600;
		word-break: break-all;
	}

	.status-control {
		min-width: 135px;
		text-align: right;
	}

	.status-switch {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
	}

	.status-switch-text {
		color: #606266;
		font-size: 13px;
		white-space: nowrap;
	}

	.status-loading {
		margin-left: 8px;
		color: #409eff;
	}

	.account-details {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 16px;
		margin: 24px 0 0;

		dt {
			margin-bottom: 6px;
			color: #909399;
			font-size: 13px;
		}

		dd {
			margin: 0;
			word-break: break-word;
		}
	}

	.sync-error dd,
	.field-error,
	.delete-button {
		color: #f56c6c;
	}

	.field-error,
	.form-help {
		margin: 6px 0 0;
		font-size: 12px;
		line-height: 1.5;
	}

	.form-help {
		color: #909399;
	}

	.account-empty {
		min-height: 116px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: center;
		gap: 14px;

		p {
			margin: 0;
			color: #606266;
		}
	}

	.disabled-notice {
		margin-top: 12px;
	}

	.customer-tags {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
	}
}

@media (max-width: 640px) {
	.ai-email-page {
		.section-heading,
		.account-overview {
			flex-direction: column;
		}

		.status-control {
			text-align: left;
		}

		.status-switch {
			justify-content: flex-start;
		}
	}
}
</style>
